import React, { Fragment, useState } from "react";
import { styled } from "@mui/system";
import Nav from "react-bootstrap/Nav";
import { Link, useLocation } from "react-router-dom";
import { Typography } from "@mui/material";
import {
  SCHOOL_LOGO_DEFAULT,
  MARKETPLACE_CONSTANTS,
} from "../config/constants";
import {
  DISPLAY_FLEX,
  FLEX_DIRECTION_COLUMN,
  MAX_WIDTH,
  TEXT_DECORATION_NONE,
} from "../config/styles";
import { colors } from "../config/colors";
import { ROUTES } from "../config/routes";
import { GlobalSideBar } from "./GlobalSideBar";
import { DownArrowLogo, UpArrowLogo } from "../assets/icons";

const MerchantSideBarStyled = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.secondary.contrastText,
  width: "210px !important",
  left: "68px !important",
  position: "fixed",
  "& .merchant-sidebar": {
    zIndex: 999,
    position: "fixed",
    height: "100%",
    backgroundColor: theme.palette.background.default,
    boxShadow: "0px 5px 10px #e7e4e3",
    float: "left",
    padding: "1rem",
    width: "188px !important",
  },
  "& .options-container": {
    diplay: DISPLAY_FLEX,
    flexDirection: FLEX_DIRECTION_COLUMN,
    marginTop: "1rem",
    gap: "4px",
    width: MAX_WIDTH,
  },
  "& .options-item": {
    width: MAX_WIDTH,
    borderRadius: "4px",
    padding: "8px 12px",
    textDecoration: "none",
    color: colors.nonActive,
  },
  "& .options-item-active": {
    width: MAX_WIDTH,
    borderRadius: "4px",
    padding: "8px 12px",
    textDecoration: "none",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.secondary.contrastText,
  },
}));

const merchantNavList = [
  {
    title: "Student data",
    link: ROUTES.STUDENT_DATA,
    eventId: "link-1",
    disabled: false,
  },
  {
    title: "User Management",
    link: ROUTES.USER_MANAGEMENT,
    eventId: "link-2",
    disabled: false,
  },
  {
    title: "Smart plans",
    link: ROUTES.SMART_PLANS,
    eventId: "link-7",
    disabled: false,
  },
  {
    title: "Payment Links",
    link: ROUTES.PAY_BY_LINK,
    eventId: "link-9",
    disabled: false,
  },
  // will use in future
  // {
  //   title: "Pricing",
  //   link: ROUTES.PRICING,
  //   eventId: "link-10",
  //   disabled: false,
  // },
  {
    title: "Direct debit",
    link: ROUTES.DIRECT_DEBIT,
    eventId: "link-8",
    disabled: false,
  },
  {
    title: "Configurations",
    link: ROUTES.STUDENT_APP_CONFIG,
    eventId: "link-3",
    disabled: false,
  },
  {
    title: "Notifications",
    link: ROUTES.NOTIFICATIONS,
    eventId: "link-4",
    disabled: false,
  },
  {
    title: "Reports",
    link: ROUTES.REPORTS,
    eventId: "link-5",
    disabled: false,
  },
  {
    title: "Marketplace",
    link: ROUTES.MARKETPLACE,
    eventId: "link-6",
    disabled: false,
    hasSubmenu: true,
    submenu: [
      {
        title: "Products",
        link: ROUTES.MARKETPLACE,
        eventId: "link-6-1",
      },
      {
        title: "Categories",
        link: ROUTES.CATEGORIES,
        eventId: "link-6-2",
      },
    ],
  },
];

export const MerchantSideBar = () => {
  const [noIcon, setNoIcon] = React.useState(false);
  const location = useLocation();
  const activeItem = location.pathname;
  let isMarketPlace = activeItem.includes(ROUTES.MARKETPLACE_OUTLET);

  // Check if we're on any marketplace route (products or categories)
  const isMarketplaceRoute =
    activeItem.includes(ROUTES.MARKETPLACE) ||
    activeItem.includes(ROUTES.CATEGORIES);
  // Auto-expand marketplace submenu if on marketplace route
  const [marketplaceExpanded, setMarketplaceExpanded] =
    useState(isMarketplaceRoute);

  React.useEffect(() => {
    const icon = localStorage.getItem("icon");
    setNoIcon(icon == "null" || icon == "");
  }, []);

  // Auto-expand when navigating to marketplace routes
  React.useEffect(() => {
    if (isMarketplaceRoute) {
      setMarketplaceExpanded(true);
    }
  }, [isMarketplaceRoute]);

  const icon = localStorage.getItem("icon");
  const role = localStorage.getItem("role");
  const isSuperAdmin = role === "SUPERADMIN";
  const schoolAdmin = role === "SCHOOLADMIN";
  const configShow = isSuperAdmin || role === "SALESADMIN";
  const isSuperAdminWithMarketPlace = false;
  // isSuperAdmin && isMarketplaceRoute;
  const schoolName = configShow
    ? localStorage.getItem("superAdminSchoolName")
    : localStorage.getItem("schoolAdminSchoolName");

  const handleMarketplaceClick = (e, merchant) => {
    if (merchant.hasSubmenu) {
      e.preventDefault();
      setMarketplaceExpanded(!marketplaceExpanded);
    }
  };

  return (
    <Fragment>
      <GlobalSideBar type="small" />
      <MerchantSideBarStyled>
        <div>
          <div className="merchant-sidebar">
            <Nav variant="pills" defaultActiveKey="/link-1">
              {!isSuperAdminWithMarketPlace && (
                <div>
                  {isMarketPlace ? null : (
                    <img
                      src={noIcon ? SCHOOL_LOGO_DEFAULT : icon}
                      className="rounded-circle mx-2"
                      width="56px"
                      height="56px"
                      alt="school logo"
                    />
                  )}

                  <Typography
                    variant="h5"
                    sx={{
                      padding: "8px 12px 0 12px",
                      width: "156px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {isMarketPlace ? MARKETPLACE_CONSTANTS.HEADER : schoolName}
                  </Typography>
                </div>
              )}

              <Nav className="options-container">
                {isMarketPlace ? (
                  <Nav.Link
                    eventKey={"link-6"}
                    as="div"
                    bsPrefix={"options-item-active"}
                  >
                    <Link
                      to={ROUTES.MARKETPLACE}
                      style={{ textDecoration: TEXT_DECORATION_NONE }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.custom,
                        }}
                      >
                        {"Products"}
                      </Typography>
                    </Link>
                  </Nav.Link>
                ) : (
                  merchantNavList.map((merchant) => {
                    const isSubmenuItemActive =
                      merchant.hasSubmenu &&
                      merchant.submenu?.some((sub) => activeItem === sub.link);
                    const isItemActive =
                      (activeItem === merchant.link ||
                        activeItem.startsWith(merchant.link + "/") ||
                        isSubmenuItemActive) &&
                      !merchant.disabled;
                    const resistConfigMenu =
                      !configShow &&
                      (merchant.eventId === "link-3" ||
                        merchant.eventId === "link-2");
                    const hideAllMenu = false;
                    // isSuperAdminWithMarketPlace && merchant.eventId !== "link-6";

                    const hideMarketplaceMenu = false;
                    // !isSuperAdminWithMarketPlace &&
                    // merchant.eventId === "link-6";
                    const showMarketplaceMenu =
                      schoolAdmin && merchant.eventId === "link-6";

                    const hideSmartPlansMenu =
                      !isSuperAdmin &&
                      !schoolAdmin &&
                      merchant.eventId === "link-7";

                    const hideDirectDebitsMenu =
                      !isSuperAdmin &&
                      !schoolAdmin &&
                      merchant.eventId === "link-8";

                    const hidePayByLinkMenu =
                      !isSuperAdmin &&
                      !schoolAdmin &&
                      merchant.eventId === "link-9";

                    // const hidePricingMenu =
                    //   !isSuperAdmin && merchant.eventId === "link-10";

                    if (
                      resistConfigMenu ||
                      hideAllMenu ||
                      hideSmartPlansMenu ||
                      hideDirectDebitsMenu ||
                      hidePayByLinkMenu ||
                      // hidePricingMenu ||
                      (hideMarketplaceMenu && !showMarketplaceMenu)
                    )
                      return null;

                    // Render menu item with submenu
                    if (merchant.hasSubmenu) {
                      return (
                        <Fragment key={merchant.eventId}>
                          <Nav.Link
                            eventKey={merchant.eventId}
                            as="div"
                            bsPrefix={
                              isItemActive
                                ? "options-item-active"
                                : "options-item"
                            }
                            onClick={(e) => handleMarketplaceClick(e, merchant)}
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                color: isItemActive
                                  ? colors.custom
                                  : colors.nonActive,
                              }}
                            >
                              {merchant.title}
                            </Typography>
                            {marketplaceExpanded ? (
                              <UpArrowLogo />
                            ) : (
                              <DownArrowLogo />
                            )}
                          </Nav.Link>

                          {/* Submenu items */}
                          {marketplaceExpanded &&
                            merchant.submenu?.map((subItem) => {
                              const isSubItemActive =
                                activeItem === subItem.link;
                              return (
                                <Nav.Link
                                  eventKey={subItem.eventId}
                                  key={subItem.eventId}
                                  as="div"
                                  bsPrefix={
                                    isSubItemActive
                                      ? "options-item-active"
                                      : "options-item"
                                  }
                                  style={{ paddingLeft: "24px" }}
                                >
                                  <Link
                                    to={subItem.link}
                                    style={{
                                      textDecoration: TEXT_DECORATION_NONE,
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        color: isSubItemActive
                                          ? colors.custom
                                          : colors.nonActive,
                                      }}
                                    >
                                      {subItem.title}
                                    </Typography>
                                  </Link>
                                </Nav.Link>
                              );
                            })}
                        </Fragment>
                      );
                    }

                    // Render regular menu item
                    return (
                      <Nav.Link
                        eventKey={merchant.eventId}
                        key={merchant.eventId}
                        as="div"
                        bsPrefix={
                          isItemActive ? "options-item-active" : "options-item"
                        }
                        style={{
                          opacity: merchant.disabled
                            ? colors.disabledOpacity
                            : 1,
                          cursor: merchant.disabled ? "not-allowed" : "pointer",
                        }}
                      >
                        {merchant.disabled ? (
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.nonActive,
                            }}
                          >
                            {merchant.title}
                          </Typography>
                        ) : (
                          <Link
                            to={merchant.link}
                            style={{ textDecoration: TEXT_DECORATION_NONE }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                color: isItemActive
                                  ? colors.custom
                                  : colors.nonActive,
                              }}
                            >
                              {merchant.title}
                            </Typography>
                          </Link>
                        )}
                      </Nav.Link>
                    );
                  })
                )}
              </Nav>
            </Nav>
          </div>
        </div>
      </MerchantSideBarStyled>
    </Fragment>
  );
};
